/**
 * Simple notification service for user feedback
 */
import i18n from "../i18n";
import { devLog } from "../logger";

const notificationMessageKeys = {
  highlights: "notifications.highlightsNotAvailable",
  underline: "notifications.underlinesNotAvailable",
  notes: "notifications.notesNotAvailable",
  freehand_drawing: "notifications.drawingNotAvailable",
  bookmarks: "notifications.bookmarksNotAvailable",
  search: "notifications.searchNotAvailable",
  comments: "notifications.commentsNotAvailable",
  page_out_of_range: "notifications.pageOutOfRange",
  ai_assistant: "notifications.aiAssistantComingSoon",
  download: "notifications.featureComingSoon",
  citation: "notifications.featureComingSoon",
  print: "notifications.featureComingSoon",
  reading_group: "notifications.featureComingSoon",
  export: "notifications.featureComingSoon",
  share: "notifications.featureComingSoon",
  add_note: "notifications.featureComingSoon",
  notes_share: "notifications.featureComingSoon",
  notes_share_item: "notifications.featureComingSoon",
  text_box: "notifications.textBoxComingSoon",
  text_to_speech: "notifications.textToSpeechComingSoon",
};

/**
 * Show a "not available" notification for a feature
 * @param {string} featureName
 */
export const showFeatureNotAllowedNotification = (featureName) => {
  const messageKey =
    notificationMessageKeys[featureName] || "notifications.featureNotAllowed";

  dispatchNotification({ messageKey, type: "warning" });
};

/**
 * Show a "coming soon" notification for an unfinished feature
 * @param {string} featureName
 */
export const showComingSoonNotification = (featureName) => {
  const messageKey =
    notificationMessageKeys[featureName] || "notifications.featureComingSoon";
  dispatchNotification({ messageKey, type: "info" });
};

/**
 * Show a generic error notification
 * @param {string} message - User-friendly error message (or translation key)
 * @param {boolean} isTranslationKey - If true, treat message as a translation key
 */
export const showErrorNotification = (message, isTranslationKey = false) => {
  dispatchNotification({
    message: isTranslationKey ? undefined : message,
    messageKey: isTranslationKey ? message : undefined,
    type: "error",
  });
};

/**
 * Show a short informational toast (e.g. copy success).
 * @param {string} messageKey
 * @param {object} [messageParams]
 */
export const showInfoNotification = (messageKey, messageParams) => {
  dispatchNotification({
    messageKey,
    messageParams,
    type: "info",
  });
};

/**
 * Show session expired notification (generic, no token terminology)
 */
export const showSessionExpiredNotification = () => {
  dispatchNotification({
    messageKey: "notifications.sessionExpired",
    type: "error",
  });
};

/**
 * Show operation failed notification
 * @param {string} operation - What the user was trying to do (e.g., "save note", "add bookmark")
 */
export const showOperationFailedNotification = (operation) => {
  dispatchNotification({
    messageKey: "notifications.operationFailed",
    messageParams: {
      operation:
        operation || i18n.t("notifications.operations.completeAction"),
    },
    type: "error",
  });
};

/**
 * Show a toast when copy exceeds the allowed character limit.
 * @param {number} limit - The max characters allowed for copy
 */
export const showCopyLimitExceededNotification = (limit) => {
  dispatchNotification({
    messageKey: "notifications.copyLimitExceeded",
    messageParams: { limit },
    type: "warning",
  });
};

/**
 * Show a toast when copy is not allowed by policy.
 */
export const showCopyNotAllowedNotification = () => {
  dispatchNotification({
    messageKey: "notifications.copyNotAllowed",
    type: "warning",
  });
};

function dispatchNotification(payload) {
  if (typeof window === "undefined") {
    // Fallback for non-browser environments
    console.warn("Notification dispatch in non-browser environment:", payload);
    return;
  }
  window.dispatchEvent(
    new CustomEvent("app:notification", { detail: payload }),
  );
  devLog("✅ Notification event dispatched");
}
